import React, { useState } from 'react';

import { useInView } from '../../hooks/useInView';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { JoinColumn } from './JoinColumn';
import type { JoinSectionData, JoinSide } from './joinSectionData';

import './JoinSection.css';

type JoinSectionProps = {
    data: JoinSectionData;
};

export function JoinSection({ data }: JoinSectionProps) {
    const [hovered, setHovered] = useState<JoinSide | null>(null);
    const isDesktop = useMediaQuery('(min-width: 901px)');
    const hasHover = useMediaQuery('(hover: hover)');
    const [athleteRef, athleteInView] = useInView<HTMLDivElement>();
    const [brandRef, brandInView] = useInView<HTMLDivElement>();
    const visible: Record<JoinSide, boolean> = { athlete: athleteInView, brand: brandInView };
    const colRefs: Record<JoinSide, React.RefObject<HTMLDivElement | null>> = {
        athlete: athleteRef,
        brand: brandRef,
    };

    const containerClass = hovered
        ? 'join-section__container join-section__container--active'
        : 'join-section__container';

    return (
        <section className="join-section">
            <div className="join-section__frame">
                {isDesktop && (
                    <>
                        {data.backgroundVideo && (
                            <video
                                className={`join-section__video${hovered ? ' join-section__video--hidden' : ''}`}
                                src={data.backgroundVideo}
                                autoPlay
                                loop
                                muted
                                playsInline
                                aria-hidden="true"
                                poster={data.posterResourcePath || undefined}
                            />
                        )}
                        {data.columns.map((column) => (
                            column.hoverVideo && (
                                <video
                                    key={column.side}
                                    className={`join-section__hover-bg${hovered === column.side ? ' join-section__hover-bg--visible' : ''}`}
                                    src={column.hoverVideo}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    aria-hidden="true"
                                />
                            )
                        ))}
                    </>
                )}
                <div className={containerClass}>
                    {data.columns.map((column, idx) => (
                        <React.Fragment key={column.side}>
                            <JoinColumn
                                ref={colRefs[column.side]}
                                data={column}
                                isHovered={hovered === column.side}
                                isActive={hovered === column.side}
                                isInactive={hovered !== null && hovered !== column.side}
                                isVisible={visible[column.side]}
                                showVideo={!isDesktop}
                                onMouseEnter={hasHover ? () => setHovered(column.side) : undefined}
                                onMouseLeave={hasHover ? () => setHovered(null) : undefined}
                            />
                            {idx < data.columns.length - 1 && (
                                <div className="join-section__divider" aria-hidden="true" />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </section>
    );
}
